import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Gift,
  Utensils,
  Beer,
  Coffee,
  Sparkles,
  Train,
  ShieldCheck,
  Medal,
  Ticket,
  Footprints,
  Flag,
  Building2,
  Trophy,
  CalendarDays,
  MapPin,
  ExternalLink,
  CheckCircle2,
  Users,
} from 'lucide-react';
import SourcesTag from './SourcesTag';
import SpectatorGuideView from './SpectatorGuideView';
import type { FreebieCategory, ProofRequired, Race, RaceWeek, RaceWeekKind, Wave } from './types';

const categoryMeta: Record<FreebieCategory, { label: string; icon: React.ReactNode }> = {
  food: { label: 'Food', icon: <Utensils size={14} /> },
  drink: { label: 'Drinks', icon: <Beer size={14} /> },
  recovery: { label: 'Recovery', icon: <Sparkles size={14} /> },
  transport: { label: 'Transport', icon: <Train size={14} /> },
  treat: { label: 'Treats', icon: <Coffee size={14} /> },
};

const kindMeta: Record<RaceWeekKind, { label: string; icon: React.ReactNode }> = {
  official: { label: 'Official', icon: <Flag size={13} /> },
  race: { label: 'Race', icon: <Medal size={13} /> },
  shakeout: { label: 'Shakeouts', icon: <Footprints size={13} /> },
  brand: { label: 'Pop-ups & parties', icon: <Sparkles size={13} /> },
  city: { label: 'City', icon: <Building2 size={13} /> },
  sport: { label: 'Sport', icon: <Trophy size={13} /> },
};

const proofLabel: Record<ProofRequired, string> = {
  medal: 'Show your medal',
  bib: 'Show your bib',
  'race-number': 'Show race number',
  'medal-or-bib': 'Show medal or bib',
  none: 'Open to all',
};

const proofIcon: Record<ProofRequired, React.ReactNode> = {
  medal: <Medal size={11} />,
  bib: <Ticket size={11} />,
  'race-number': <Ticket size={11} />,
  'medal-or-bib': <Medal size={11} />,
  none: <Gift size={11} />,
};

export type RaceWeekTab = 'perks' | 'calendar' | 'spectators';

export const RACE_WEEK_TAB_META: Record<RaceWeekTab, { label: string; icon: React.ReactNode }> = {
  perks: { label: 'Perks', icon: <Gift size={18} /> },
  calendar: { label: 'Calendar', icon: <CalendarDays size={18} /> },
  spectators: { label: 'Spectators', icon: <Users size={18} /> },
};

/** Second line of each tab button, matching the main section tabs' sub-labels. */
export const raceWeekTabSub = (tab: RaceWeekTab, raceWeek: RaceWeek): string =>
  tab === 'perks'
    ? `${raceWeek.freebies.length} free offers`
    : tab === 'calendar'
      ? `${raceWeek.events.length} events`
      : `${raceWeek.spectators?.spots.length ?? 0} cheer spots`;

/** Tabs with content for this race, perks first (the default). */
export const raceWeekTabs = (raceWeek: RaceWeek): RaceWeekTab[] =>
  (['perks', 'calendar', 'spectators'] as RaceWeekTab[]).filter((t) =>
    t === 'perks' ? raceWeek.freebies.length > 0 : t === 'calendar' ? raceWeek.events.length > 0 : !!raceWeek.spectators,
  );

const heroTitles: Record<RaceWeekTab, React.ReactNode> = {
  perks: (
    <>
      Free Stuff <span className="fuel-amp">&amp;</span> Finisher Perks
    </>
  ),
  calendar: (
    <>
      Race-Week <span className="fuel-amp">&amp;</span> City Calendar
    </>
  ),
  spectators: (
    <>
      Where to Watch <span className="fuel-amp">&amp;</span> Cheer
    </>
  ),
};

/** Minutes past midnight for ordering a day's events; all-day and multi-day entries sort first. */
const timeSortKey = (time: string): number => {
  const match = /(\d{1,2}):(\d{2})/.exec(time);
  if (match) return Number(match[1]) * 60 + Number(match[2]);
  if (/evening/i.test(time)) return 18 * 60;
  if (/afternoon/i.test(time)) return 14 * 60;
  if (/morning/i.test(time)) return 9 * 60;
  return -1;
};

/** Formats a local YYYY-MM-DD as e.g. "Saturday 10 October" without shifting it through the viewer's timezone. */
const formatDay = (iso: string): string =>
  new Intl.DateTimeFormat('en-AU', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }).format(
    new Date(`${iso}T12:00:00Z`),
  );

const RaceWeekView: React.FC<{
  race: Race;
  raceWeek: RaceWeek;
  label: string;
  waves?: Wave[];
  tab?: RaceWeekTab;
}> = ({ race, raceWeek, label, waves, tab: requestedTab = 'perks' }) => {
  const [kind, setKind] = useState<RaceWeekKind | 'all'>('all');
  const [category, setCategory] = useState<FreebieCategory | 'all'>('all');
  const { events, freebies } = raceWeek;
  const tabs = raceWeekTabs(raceWeek);
  const tab = tabs.includes(requestedTab) ? requestedTab : tabs[0];

  const kindsPresent = (Object.keys(kindMeta) as RaceWeekKind[]).filter((k) => events.some((e) => e.kind === k));
  const byDay = useMemo(() => {
    const filtered = kind === 'all' ? events : events.filter((e) => e.kind === kind);
    const groups = new Map<string, typeof events>();
    const ordered = [...filtered].sort(
      (a, b) => a.date.localeCompare(b.date) || timeSortKey(a.time) - timeSortKey(b.time),
    );
    for (const e of ordered) {
      groups.set(e.date, [...(groups.get(e.date) ?? []), e]);
    }
    return [...groups.entries()];
  }, [events, kind]);

  const rankedFreebies = useMemo(
    () => [...freebies].sort((a, b) => (a.rank ?? Infinity) - (b.rank ?? Infinity)),
    [freebies],
  );
  const filteredFreebies =
    category === 'all' ? rankedFreebies : rankedFreebies.filter((f) => f.category === category);
  const categoriesPresent = (Object.keys(categoryMeta) as FreebieCategory[]).filter((c) =>
    freebies.some((f) => f.category === c),
  );

  return (
    <>
      <motion.section
        className="fuel-hero ev-hero free-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        <div className="fuel-hero-inner">
          <motion.div
            className="fuel-hero-eyebrow"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <span className="fuel-hero-dot" />
            <span>Around the Race</span>
            <span className="fuel-hero-sep">·</span>
            <span>{label}</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.8 }}
          >
            {heroTitles[tab]}
          </motion.h1>
          <motion.p
            className="free-hero-lede"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.55, duration: 0.6 }}
          >
            {raceWeek.lede}
          </motion.p>
        </div>
      </motion.section>

      {tab === 'calendar' && (
        <section className="ev-section">
          <p className="ev-sub">
            Official {race.city} Marathon events, shakeout runs, and what else is on in the city.{' '}
            <CheckCircle2 size={12} style={{ verticalAlign: '-1px' }} /> marks things already in your itinerary.
          </p>
          <div className="free-filter-row rw-filter-row">
            <button
              className={`free-chip ${kind === 'all' ? 'free-chip--active' : ''}`}
              onClick={() => setKind('all')}
            >
              <CalendarDays size={13} />
              <span>All · {events.length}</span>
            </button>
            {kindsPresent.map((k) => (
              <button
                key={k}
                className={`free-chip ${kind === k ? 'free-chip--active' : ''}`}
                onClick={() => setKind(k)}
              >
                {kindMeta[k].icon}
                <span>
                  {kindMeta[k].label} · {events.filter((e) => e.kind === k).length}
                </span>
              </button>
            ))}
          </div>

          <div className="rw-days">
            {byDay.map(([date, dayEvents]) => (
              <div key={date} className={`rw-day ${date === race.date ? 'rw-day--race' : ''}`}>
                <div className="rw-day-head">
                  <span className="rw-day-date">{formatDay(date)}</span>
                  {date === race.date && <span className="rw-day-badge">Race day</span>}
                </div>
                <ul className="rw-events">
                  {dayEvents.map((e, i) => (
                    <li key={i} className={`rw-event rw-event--${e.kind}`}>
                      <span className="rw-event-time">{e.time}</span>
                      <div className="rw-event-body">
                        <div className="rw-event-title-row">
                          <span className="rw-event-kind">{kindMeta[e.kind].icon}</span>
                          <h3 className="rw-event-title">{e.title}</h3>
                          {e.planned && <CheckCircle2 size={14} className="rw-event-planned" aria-label="In your itinerary" />}
                        </div>
                        <p className="rw-event-loc">
                          <MapPin size={11} /> {e.location}
                        </p>
                        <p className="rw-event-desc">{e.description}</p>
                        {(e.booking || e.url) && (
                          <p className="rw-event-booking">
                            {e.booking}
                            {e.url && (
                              <a href={e.url} target="_blank" rel="noopener noreferrer">
                                {' '}
                                Details <ExternalLink size={10} />
                              </a>
                            )}
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}

      {tab === 'spectators' && raceWeek.spectators && (
        <SpectatorGuideView
          race={race}
          guide={raceWeek.spectators}
          waves={waves ?? [{ id: 'main', label: 'Start', time: race.startTime }]}
        />
      )}

      {tab === 'perks' && (
        <>
          <section className="ev-section free-filter-section">
            <p className="ev-sub">
              {raceWeek.freebiesLede} Click the <ShieldCheck size={12} style={{ verticalAlign: '-1px' }} /> badge to see
              the sources.
            </p>
            <div className="free-filter-row">
              <button
                className={`free-chip ${category === 'all' ? 'free-chip--active' : ''}`}
                onClick={() => setCategory('all')}
              >
                <Gift size={13} />
                <span>All · {freebies.length}</span>
              </button>
              {categoriesPresent.map((cat) => (
                <button
                  key={cat}
                  className={`free-chip ${category === cat ? 'free-chip--active' : ''}`}
                  onClick={() => setCategory(cat)}
                >
                  {categoryMeta[cat].icon}
                  <span>
                    {categoryMeta[cat].label} · {freebies.filter((f) => f.category === cat).length}
                  </span>
                </button>
              ))}
            </div>
          </section>

          <section className="ev-section">
            <div className="free-grid">
              {filteredFreebies.map((f) => (
                <motion.article
                  key={f.name}
                  className={`free-card free-card--${f.category} ${f.status === 'likely' ? 'free-card--likely' : ''}`}
                  initial={{ opacity: 0, y: 12 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.4 }}
                >
                  {f.image && (
                    <div className="free-card-image">
                      <img src={f.image} alt={f.name} loading="lazy" />
                      <div className="free-card-category">
                        {categoryMeta[f.category].icon}
                        <span>{categoryMeta[f.category].label}</span>
                      </div>
                    </div>
                  )}
                  <div className="free-card-body">
                    <div className="free-card-head">
                      <div className="free-card-icon">{f.icon}</div>
                      <div className="free-card-title-wrap">
                        <h3 className="free-card-title">{f.name}</h3>
                        <span className="free-card-offer">{f.offer}</span>
                      </div>
                    </div>
                    <p className="free-card-desc">{f.description}</p>
                    <div className="free-card-meta">
                      <span className="free-card-proof">
                        {proofIcon[f.proof]}
                        {proofLabel[f.proof]}
                      </span>
                      <span className="free-card-location">{f.neighbourhood}</span>
                    </div>
                    {f.caveats && <p className="free-card-caveat">{f.caveats}</p>}
                    <div className="free-card-tags">
                      {f.status === 'likely' && <span className="free-likely-tag">Likely (not officially confirmed)</span>}
                      {f.refs.length > 0 && <SourcesTag refs={f.refs} summary={f.refsSummary} />}
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          </section>
        </>
      )}

      {tab === 'perks' && (
        <section className="ev-section free-footnote-section">
          <div className="free-footnote">
            <ShieldCheck size={14} />
            <p>{raceWeek.footnote}</p>
          </div>
        </section>
      )}

      <motion.footer
        className="footer ev-footer free-footer"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
        viewport={{ once: true }}
      >
        <div className="footer-content">
          <Gift size={28} />
          <h3>Finisher perks</h3>
          <p>Bring your bib, medal and photo ID on race day and the day after.</p>
        </div>
      </motion.footer>
    </>
  );
};

export default RaceWeekView;
